/**
 * Referral Service
 *
 * Handles referral code generation, validation, tracking, and bonus distribution.
 * All operations write to Supabase — no local state.
 *
 * Flow:
 * 1. User gets a unique referral code on signup/onboarding
 * 2. They share the code (or link: app.juuniai.com/signup?ref=CODE)
 * 3. Referee enters code at signup → validated → referral record created
 * 4. Milestones tracked: SignedUp → AssessmentPassed → FirstJobCompleted → BonusPaid
 * 5. Both parties get bonus when referee completes first job
 */

import { supabase } from '../lib/supabase';
import { safeQuery } from '../utils/safeSupabase';

// ============================================================
// CODE GENERATION
// ============================================================

/**
 * Generate a human-readable referral code
 * Format: PREFIX-RANDOM (e.g., MAYA-A3F2B1)
 */
function generateCode(name) {
  const prefix = name.split(' ')[0].toUpperCase().slice(0, 4);
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No ambiguous chars (0/O, 1/I/L)
  let suffix = '';
  const array = new Uint8Array(6);
  crypto.getRandomValues(array);
  for (let i = 0; i < 6; i++) {
    suffix += chars[array[i] % chars.length];
  }
  return `${prefix}-${suffix}`;
}

/**
 * Hash a referral code using SHA-256
 * We store the hash, not the code — so even DB compromise doesn't expose codes
 */
async function hashCode(code) {
  const encoder = new TextEncoder();
  const data = encoder.encode(code.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ============================================================
// REFERRAL OPERATIONS
// ============================================================

/**
 * Generate and save a referral code for a user
 * Called once during onboarding
 */
export async function generateReferralCode(userId, userName) {
  // Check if user already has a code
  const { data: existing } = await safeQuery(
    supabase
      .from('individual')
      .select('referral_code')
      .eq('id', userId)
      .single(),
    'referral-checkExistingCode'
  );

  if (existing?.referral_code) {
    return { code: existing.referral_code, alreadyExists: true };
  }

  const code = generateCode(userName);
  const hash = await hashCode(code);

  const { error } = await supabase
    .from('individual')
    .update({ referral_code: code, referral_code_hash: hash })
    .eq('id', userId);

  if (error) {
    console.error('Failed to save referral code:', error);
    return { error: 'Failed to generate code' };
  }

  console.log(`✅ Referral code generated for ${userName}: ${code}`);
  return { code, alreadyExists: false };
}

/**
 * Validate a referral code entered by a new user
 * Returns the referrer's info if valid
 */
export async function validateReferralCode(code) {
  if (!code || code.trim().length < 4) {
    return { valid: false, error: 'Invalid code format' };
  }

  const hash = await hashCode(code);

  const { data, error } = await supabase
    .from('individual')
    .select('id, name, primary_domain, referral_code')
    .eq('referral_code_hash', hash)
    .single();

  if (error || !data) {
    return { valid: false, error: 'Referral code not found' };
  }

  return {
    valid: true,
    referrerId: data.id,
    referrerName: data.name,
    referrerDomain: data.primary_domain,
  };
}

/**
 * Create a referral record after referee signs up with a valid code
 */
export async function createReferral(referrerId, refereeId, jobId = null) {
  // Prevent self-referral
  if (referrerId === refereeId) {
    return { error: 'Cannot refer yourself' };
  }

  const { data: authData } = await supabase.auth.getUser();
  const currentUserId = authData?.user?.id || null;

  if (!currentUserId || !refereeId || currentUserId !== refereeId) {
    return { error: 'Signup referral creation must run as the signed-in referee' };
  }

  const { data: result, error: rpcError } = await supabase.rpc('create_signup_referral', {
    p_referrer_id: referrerId,
    p_job_id: jobId,
  });

  if (rpcError) {
    console.error('create_signup_referral RPC failed:', rpcError);
    return { error: rpcError.message || 'Failed to create referral' };
  }

  if (result && !result.success) {
    return { error: result.error || 'Failed to create referral' };
  }

  return {
    referral: result ? {
      id: result.referral_id,
      status: result.status,
      org_id: result.org_id,
    } : null,
    alreadyExists: Boolean(result?.already_exists),
  };
}

/**
 * Update referral milestone
 * Called when referee hits a milestone (assessment passed, first job completed)
 */
export async function updateReferralMilestone(refereeId, milestone, jobId = null) {
  const { data: result, error: rpcError } = await supabase.rpc('advance_referral_milestone', {
    p_referee_id: refereeId,
    p_milestone: milestone,
    p_job_id: jobId,
  });

  if (rpcError) {
    console.error('advance_referral_milestone RPC failed:', rpcError);
    return { error: rpcError.message || 'Failed to update referral milestone' };
  }

  if (result && !result.success) {
    return { error: result.error || 'Failed to update referral milestone' };
  }

  return {
    success: true,
    milestone,
    skipped: Boolean(result?.skipped),
    alreadyAdvanced: Boolean(result?.already_advanced),
    bonusPaid: Boolean(result?.bonus_paid),
    reason: result?.reason || null,
    referralId: result?.referral_id || null,
    status: result?.status || null,
  };
}

// ============================================================
// QUERIES (for frontend display)
// ============================================================

/**
 * Get all referrals for a user (as referrer)
 */
export async function getUserReferrals(userId) {
  const { data, error } = await supabase
    .from('referral')
    .select('*, referee:referee_id(name, primary_domain, status)')
    .eq('referrer_id', userId)
    .order('invited_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch referrals:', error);
    return [];
  }

  return data;
}

/**
 * Get referral stats for a user
 */
export async function getReferralStats(userId) {
  const referrals = await getUserReferrals(userId);

  return {
    total: referrals.length,
    signedUp: referrals.filter(r => r.status !== 'Invited').length,
    completed: referrals.filter(r => ['FirstJobCompleted', 'BonusPaid'].includes(r.status)).length,
    bonusesPaid: referrals.filter(r => r.status === 'BonusPaid').length,
    totalEarnings: referrals
      .filter(r => r.status === 'BonusPaid')
      .reduce((sum, r) => sum + (r.referrer_bonus_amount || 0), 0),
    pending: referrals.filter(r => ['Invited', 'SignedUp', 'AssessmentPassed'].includes(r.status)).length,
  };
}
